/**
 * Entry editor — corrections, not capture.
 *
 * Same model as the Add screen: an entry is a list of lines. Shaped differently
 * on purpose, though. Add is optimised for speed and asks about a category only
 * when it cannot avoid it; here every line shows its category as a plain field,
 * because you are on this screen precisely because something is wrong, and the
 * thing that is wrong is often where it was filed.
 *
 * Re-filing a line here moves THIS purchase only. The item keeps its own
 * category for next time — change that on the Items screen (AGENTS.md §3.5b).
 *
 * Entries recorded before item tracking existed have splits but no lines. They
 * open as unnamed lines carrying the split's amount, so they can be corrected
 * without anyone having to invent a shopping list that was never typed.
 */
import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import type {
  Category,
  Entry,
  EntryItemInput,
  Household,
  Merchant,
  PaymentMeans,
  UpdateEntry,
} from '@et/contracts';
import {
  divideRound,
  formatMoney,
  ITEM_UNITS,
  money,
  parseMoney,
  parseQuantity,
  QUANTITY_SCALE,
  type ItemUnit,
  type Money,
} from '@et/core';
import { api, messageOf, type Identity } from './api.js';

interface Props {
  identity: Identity;
  household: Household;
  categories: Category[];
  entry: Entry;
  merchants: Merchant[];
  means: PaymentMeans[];
  onClose: () => void;
  onSaved: () => void;
}

interface Row {
  key: string;
  name: string;
  productId: string | null;
  categoryId: string;
  quantity: string;
  unitPrice: string;
  unit: ItemUnit;
}

const UNIT_LABELS: Record<ItemUnit, string> = {
  PCS: 'pcs',
  KG: 'kg',
  G: 'g',
  L: 'L',
  ML: 'ml',
};

function tryParse(value: string, currency: string): Money | null {
  try {
    return parseMoney(value.trim(), currency);
  } catch {
    return null;
  }
}

function lineTotalOf(row: Row, currency: string): Money | null {
  const price = tryParse(row.unitPrice, currency);
  if (!price || price.amount <= 0n) return null;
  try {
    return money(divideRound(parseQuantity(row.quantity) * price.amount, QUANTITY_SCALE), currency);
  } catch {
    return null;
  }
}

/**
 * The entry as rows. A split with lines becomes one row per line; a split
 * without becomes a single unnamed row carrying its amount, so no money is
 * invented and none is lost.
 */
function rowsOf(entry: Entry): Row[] {
  return entry.splits.flatMap((split) => {
    if (split.items.length === 0) {
      return [
        {
          key: split.id,
          name: '',
          productId: null,
          categoryId: split.categoryId,
          quantity: '1',
          unitPrice: split.amount,
          unit: 'PCS' as ItemUnit,
        },
      ];
    }

    return split.items.map((item) => ({
      key: item.id,
      name: item.productName ?? item.rawText ?? '',
      productId: item.productId,
      categoryId: split.categoryId,
      // A line with no unit price cannot be expressed as quantity × price
      // without inventing a number, so it collapses to one at its own total.
      // The money is what must survive; the count is what can be re-typed.
      quantity: item.unitPrice === null ? '1' : item.quantity,
      unitPrice: item.unitPrice ?? item.lineTotal,
      unit: item.unit,
    }));
  });
}

export function EntryEditor({
  identity,
  household,
  categories,
  entry,
  merchants,
  means,
  onClose,
  onSaved,
}: Props) {
  const currency = household.baseCurrency;

  const [rows, setRows] = useState<Row[]>(() => rowsOf(entry));
  const [merchant, setMerchant] = useState(entry.merchantName ?? '');
  const [note, setNote] = useState(entry.note ?? '');
  const [spentBy, setSpentBy] = useState(entry.spentBy);
  // Archived means are still offered HERE, unlike on the Add screen: this
  // entry may already be paid with one, and the editor must not silently
  // change it just because the list moved on.
  const [meansId, setMeansId] = useState(entry.paymentMeansId ?? '');
  const [spentAt, setSpentAt] = useState(entry.spentAt);
  const [isPrivate, setIsPrivate] = useState(entry.isPrivate);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Categories are one level, so every live one is pickable. Income sources and
  // spending categories are different worlds, so only one side is ever offered.
  const choices = useMemo(
    () =>
      categories
        .filter(
          (c) =>
            !c.archived &&
            (entry.type === 'INCOME' ? c.bucket === 'INCOME' : c.bucket !== 'INCOME'),
        )
        .sort((a, b) => a.name.localeCompare(b.name)),
    [categories, entry.type],
  );

  const lineTotals = useMemo(() => rows.map((row) => lineTotalOf(row, currency)), [rows, currency]);

  const total = useMemo(() => {
    let running = 0n;
    let any = false;
    for (const line of lineTotals) {
      if (!line) continue;
      running += line.amount;
      any = true;
    }
    return any ? money(running, currency) : null;
  }, [lineTotals, currency]);

  function updateRow(key: string, patch: Partial<Row>) {
    setRows((current) => current.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  }

  /**
   * An expense that already names a merchant may not be stripped of it — the
   * same rule the API enforces. One recorded before the rule existed can still
   * be saved without one, so fixing a date on an old entry does not require
   * inventing a shop.
   */
  const merchantRequired = entry.type !== 'INCOME' && Boolean(entry.merchantName);

  const valid =
    rows.length > 0 &&
    total !== null &&
    total.amount > 0n &&
    (!merchantRequired || merchant.trim().length > 0) &&
    rows.every((row, index) => Boolean(row.categoryId) && Boolean(lineTotals[index]));

  async function save(event: FormEvent) {
    event.preventDefault();
    if (!total) return;
    setError(null);

    // Grouped by category, exactly as the Add screen does it — the word
    // "split" stays out of the interface on both screens.
    const groups = new Map<string, { categoryId: string; amount: bigint; items: EntryItemInput[] }>();

    rows.forEach((row, index) => {
      const line = lineTotals[index];
      if (!line) return;

      const named = row.name.trim();
      const existing = groups.get(row.categoryId);
      const item = named
        ? {
            ...(row.productId ? { productId: row.productId } : {}),
            productName: named,
            quantity: row.quantity.trim() || '1',
            unit: row.unit,
            unitPrice: row.unitPrice.trim(),
            lineTotal: formatMoney(line),
          }
        : null;

      if (existing) {
        existing.amount += line.amount;
        if (item) existing.items.push(item);
      } else {
        groups.set(row.categoryId, {
          categoryId: row.categoryId,
          amount: line.amount,
          items: item ? [item] : [],
        });
      }
    });

    const body: UpdateEntry = {
      // Sent so the server can refuse a stale write rather than clobbering
      // whatever another device saved while this form was open.
      version: entry.version,
      spentAt,
      spentBy,
      merchantName: merchant.trim() || null,
      note: note.trim() || null,
      amount: formatMoney(total),
      paymentMeansId: entry.type === 'INCOME' ? null : meansId || null,
      isPrivate: entry.type === 'INCOME' ? false : isPrivate,
      splits: [...groups.values()].map((group) => ({
        categoryId: group.categoryId,
        amount: formatMoney(money(group.amount, currency)),
        items: group.items,
      })),
    };

    setBusy(true);
    try {
      await api.updateEntry(identity, entry.id, body);
      onSaved();
      onClose();
    } catch (e: unknown) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label="Edit entry">
      <div className="modal">
        <div className="modal-head">
          <h2>Edit entry</h2>
          <button type="button" className="icon-btn" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <form className="editor" onSubmit={save}>
          {/* Same shape as the Add screen's field: currency first and quiet,
              figure second. Two screens that show the same number should not
              show it two different ways round. */}
          <div className="editor-total">
            <span className="field-label">Total</span>
            <strong>
              <span className="amount-currency">{currency}</span>{' '}
              {total ? formatMoney(total) : '0.00'}
            </strong>
          </div>

          <div className="lines">
            {rows.map((row, index) => {
              const line = lineTotals[index];

              return (
                <div className="line" key={row.key}>
                  <div className="line-top">
                    <input
                      className="line-name"
                      autoComplete="off"
                      placeholder="Unnamed line"
                      value={row.name}
                      onChange={(e) => updateRow(row.key, { name: e.target.value })}
                      aria-label="Item"
                    />
                    {rows.length > 1 && (
                      <button
                        type="button"
                        className="icon-btn"
                        onClick={() => setRows((c) => c.filter((r) => r.key !== row.key))}
                        aria-label={`Remove ${row.name || 'line'}`}
                      >
                        ×
                      </button>
                    )}
                  </div>

                  <div className="line-bottom">
                    <input
                      className="line-qty"
                      inputMode="decimal"
                      value={row.quantity}
                      onChange={(e) => updateRow(row.key, { quantity: e.target.value })}
                      aria-label="How many"
                    />
                    {row.quantity.trim() !== '1' && (
                      <select
                        className="line-unit"
                        value={row.unit}
                        onChange={(e) => updateRow(row.key, { unit: e.target.value as ItemUnit })}
                        aria-label="Unit"
                      >
                        {ITEM_UNITS.map((unit) => (
                          <option key={unit} value={unit}>
                            {UNIT_LABELS[unit]}
                          </option>
                        ))}
                      </select>
                    )}
                    <span className="line-x" aria-hidden="true">
                      ×
                    </span>
                    <input
                      className="line-price"
                      inputMode="decimal"
                      value={row.unitPrice}
                      onChange={(e) => updateRow(row.key, { unitPrice: e.target.value })}
                      aria-label="Price"
                    />
                    {line && row.quantity.trim() !== '1' && (
                      <span className="line-total">= {formatMoney(line)}</span>
                    )}
                  </div>

                  {/* Income has exactly one category, so there is nothing to
                      pick — the source is what identifies it. */}
                  {entry.type !== 'INCOME' && (
                    <select
                      value={row.categoryId}
                      onChange={(e) => updateRow(row.key, { categoryId: e.target.value })}
                      aria-label="Category for this line"
                    >
                      <option value="">Category…</option>
                      {choices.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
              );
            })}

            <button
              type="button"
              className="chip chip-ghost add-line"
              onClick={() =>
                setRows((c) => [
                  ...c,
                  {
                    key: crypto.randomUUID(),
                    name: '',
                    productId: null,
                    categoryId: c[0]?.categoryId ?? '',
                    quantity: '1',
                    unitPrice: '',
                    unit: 'PCS',
                  },
                ])
              }
            >
              + Another line
            </button>
          </div>

          {/* There is no Items tab any more — it merged into Categories &
              items (§3.5f(ii)). A hint that names a screen which no longer
              exists is worse than no hint. */}
          <p className="hint">
            Changing a category here moves this purchase only. To change where the item goes from
            now on, open it under Settings → Categories &amp; items.
          </p>

          <div className="grid-2">
            <label>
              <span>Date</span>
              <input type="date" value={spentAt} onChange={(e) => setSpentAt(e.target.value)} />
            </label>
            <label>
              <span>Who</span>
              <select value={spentBy} onChange={(e) => setSpentBy(e.target.value)}>
                {household.members.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.displayName}
                  </option>
                ))}
              </select>
            </label>
          </div>

          {/* Neither question has an answer for income — see the same pair
              hidden on the Add screen, and AGENTS.md §3.5d. */}
          {entry.type !== 'INCOME' && (
            <div className="grid-2">
              <label>
                <span>Paid with</span>
                <select value={meansId} onChange={(e) => setMeansId(e.target.value)}>
                  <option value="">Not recorded</option>
                  {means.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                      {m.archived ? ' · archived' : ''}
                    </option>
                  ))}
                </select>
              </label>
              <label className="inline-check">
                <input
                  type="checkbox"
                  checked={isPrivate}
                  onChange={(e) => setIsPrivate(e.target.checked)}
                />
                <span>Private</span>
              </label>
            </div>
          )}

          <label>
            <span>Merchant</span>
            <datalist id="editor-merchants">
              {merchants.map((m) => (
                <option key={m.id} value={m.name} />
              ))}
            </datalist>
            <input
              list="editor-merchants"
              autoComplete="off"
              value={merchant}
              onChange={(e) => setMerchant(e.target.value)}
              required={merchantRequired}
            />
          </label>

          <label>
            <span>Note</span>
            <input value={note} onChange={(e) => setNote(e.target.value)} />
          </label>

          {error && <p className="error">{error}</p>}

          <div className="modal-actions">
            <div className="spacer" />
            <button type="button" className="chip" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button type="submit" className="primary compact" disabled={!valid || busy}>
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
